// Player chrome traits: control panel, Pixie entry point, frame, bubble, and
// the chain (Up Next / next-video) styles that read the control-panel palette.
import { accentForTile, readPixieDefaultAccent } from "@/modules/styleColors";
import { addStyle, cssSize, type CssMap } from "@/modules/styleCss";
import type { StyleTraits } from "@/types/experience";

export function loadPanelTraits(
  target: string,
  document: Document,
  css: CssMap,
  traits: StyleTraits,
) {
  if (traits.controlPanel) {
    addStyle(
      target,
      css,
      ".control-bar",
      "background-color",
      traits.controlPanel.backgroundColor,
    );
  }

  // Publisher-configurable Pixie entry-point styling. Both the tile
  // background and the icon colour are optional; unset, the CSS defaults stand
  // (violet icon on a white tile). `!important` here wins over those CSS
  // defaults, which are deliberately NOT !important so this override can apply.
  if (traits.pixie && (traits.pixie.color || traits.pixie.backgroundColor)) {
    const pixieTileBg = traits.pixie.backgroundColor;
    if (pixieTileBg) {
      const pixieBgValue = `${pixieTileBg} !important`;
      // Both rails (immersive and desktop) — the tile is painted on the svg
      // itself (the button behind it is transparent), so colour the svg.
      addStyle(
        target,
        css,
        ".landscape-tab-btn svg.icon-chat-ai-d",
        "background",
        pixieBgValue,
      );
      addStyle(
        target,
        css,
        ".pixie-rail-btn svg.icon-chat-ai-d",
        "background",
        pixieBgValue,
      );
    }
    // Keep the icon legible on whatever tile it sits on (default white). This
    // runs even when ONLY the tile is customised: the default violet icon must
    // also clear the contrast check against a publisher's chosen tile, or a dark
    // tile could swallow an un-customised icon. Inject only when the icon is
    // custom OR the default needed adjusting for the tile -- otherwise the CSS
    // default (and its hover shade) stands untouched.
    const pixieDefaultAccent = readPixieDefaultAccent(document);
    const pixieIconInput = traits.pixie.color || pixieDefaultAccent;
    const pixieAccent = accentForTile(
      pixieIconInput,
      pixieTileBg || "#ffffff",
      document,
    );
    if (
      pixieAccent &&
      (traits.pixie.color ||
        pixieAccent.toLowerCase() !== pixieDefaultAccent.toLowerCase())
    ) {
      const pixieValue = `${pixieAccent} !important`;
      addStyle(
        target,
        css,
        ".landscape-tab-btn svg.icon-chat-ai-d",
        "fill",
        pixieValue,
      );
      addStyle(
        target,
        css,
        ".landscape-tab-btn svg.icon-chat-ai-d",
        "stroke",
        pixieValue,
      );
      addStyle(
        target,
        css,
        ".pixie-rail-btn svg.icon-chat-ai-d",
        "fill",
        pixieValue,
      );
      addStyle(
        target,
        css,
        ".pixie-rail-btn svg.icon-chat-ai-d",
        "stroke",
        pixieValue,
      );
    }
  }

  if (traits.frame) {
    const frame = traits.frame;
    if (frame.borderRadius != null) {
      addStyle(
        target,
        css,
        ".landscape .experience-container",
        "border-radius",
        cssSize(frame.borderRadius),
      );
    }
  }

  if (traits.bubble) {
    const bubble = traits.bubble;
    if (bubble.borderRadius != null) {
      addStyle(
        target,
        css,
        ".bubble-inner",
        "border-radius",
        cssSize(bubble.borderRadius),
      );
    }
  }
}

/*
 * Chain / recommendation UI (Up Next list + next-video block) reads the
 * control-panel palette so it belongs to the player chrome rather than the
 * highlight accent: the rest-state accents take controlPanel.backgroundColor
 * (the same surface as the control-bar rail) and the interactive states
 * mirror the rail's own tabs (buttonHoverType4 — surface flips, label goes to
 * the accent). Emitted only when the traits exist; unbranded experiences keep
 * the #c43e3e CSS fallbacks. Selectors are the BEM classes directly — the
 * components no longer carry the highlight-* hook classes.
 */
export function loadChainStyles(
  target: string,
  css: CssMap,
  traits: StyleTraits,
) {
  if (traits.controlPanel) {
    const surface = traits.controlPanel.backgroundColor;
    // Heading text, the block's top divider (via currentColor) and the
    // countdown ring arc (stroke follows color) all take the surface colour.
    addStyle(target, css, ".up-next__heading", "color", surface);
    addStyle(target, css, ".next-video-block", "color", surface);
    addStyle(target, css, ".next-video-block__ring-progress", "color", surface);
    // Primary CTA fill at rest (white label set in CSS).
    addStyle(
      target,
      css,
      ".next-video-block__play-now",
      "background-color",
      surface,
    );
  }

  if (traits.buttonHoverType4) {
    const hoverBg = traits.buttonHoverType4.backgroundColor;
    const hoverFg = traits.buttonHoverType4.color;
    // Play Now hover mirrors a control-bar tab: surface flips, label → accent.
    addStyle(
      target,
      css,
      ".next-video-block__play-now:hover",
      "background-color",
      hoverBg,
    );
    addStyle(
      target,
      css,
      ".next-video-block__play-now:hover",
      "color",
      hoverFg,
    );
    // Up Next card hover border + focus outline read the tab's hover accent.
    addStyle(target, css, ".up-next__item:hover", "border-color", hoverFg);
    addStyle(
      target,
      css,
      ".up-next__item:focus-visible",
      "outline-color",
      hoverFg,
    );
  }
}
